import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useScramble as useScrambleLib } from 'use-scramble';
import { Gallery } from './components/Gallery';


// ============================================
// SPLASH SCREEN COPY LINES
// ============================================
const SPLASH_LINES = [
  "the ordinary, observed",
  "stillness reveals",
  "simple hides where serenity resides",
  "joy wears a cloak",
 "within mundane lies beauty",
   "the dull world waits",
  "seeking blurs. observation reveals. ",
  "the illumination of imperfection",
  "light preceeds decay",
];


// ============================================
// ARTIST STATEMENT
// ============================================
const ARTIST_STATEMENT = `The hypnotic cadence of normal, embraced by the stillness and poise of nature.
Pure joy, shamelessly draped in reality's fabric.

I grow by collecting these seemingly mundane moments.
Imperfection's mellow beauty, illuminated, imprinted. Immortalised.
A deep sense of calm, glimpses of meaning amid the decay.

A precious portal to safety, free from the anxiety of opinion.
This is my study. These are my hands. This is my perspective.

Desire. A brutal enemy, cunningly numbing the seeking mind.
Serenity hidden in stillness, the dull won't be found by the blind.
`;
// ============================================
// CURSOR TRAIL SETTINGS
// ============================================
const TRAIL_CONFIG = {
  maxTrails: 60,
  spawnInterval: 24,
  fadeRate: 0.008,
  cursorSize: 32,
  spinSpeed: 1.6,
};

// ============================================
// NOISE EFFECT SETTINGS
// ============================================
const NOISE_CONFIG = {
  maxRadius: 800,          // max pixel radius of noise cloud from cursor
  maxParticles: 700,       // particles per frame at peak intensity
  grainMin: 1.5,           // min grain size (px)
  grainMax: 4,             // max grain size (px)
  trailFade: 0.12,         // how fast trailing noise fades (higher = faster)
  intensityDecay: 0.92,    // per-frame decay when mouse stops
  intensityBoost: 0.0012,  // intensity added per pixel of movement
  colors: ['#CF6666', '#F5F3F1', '#594e4e'],
};

// ============================================
// WAVE BACKDROP SETTINGS
// ============================================
const WAVE_CONFIG = {
  darkDuration: 1200,      // ms for cream → dark wave (moving starts)
  creamDuration: 2800,     // ms for dark → cream wave (moving stops)
  dark: { solid: '#1B1B1D', transparent: 'rgba(27, 27, 29, 0)' },
  cream: { solid: '#F5F3F1', transparent: 'rgba(245, 243, 241, 0)' },
};

// ============================================
// TEXT FADE SETTINGS (on mouse move)
// ============================================
const FADE_CONFIG = {
  fadedOpacity: 0.22,     // opacity when moving
  fadeIn: 200,            // ms to fade out (when starting to move)
  fadeOut: 1200,          // ms to fade back in (when stopping)
  clearDelay: 350,        // ms after mouse stops before clearing
};

/* CursorTrails component removed — replaced by NoiseEffect + WaveBackdrop */

// ============================================
// WAVE BACKDROP COMPONENT (color radiates from cursor)
// ============================================
// Going dark = full-screen fade. Going cream = wave radiates out from cursor.
function WaveBackdrop({ isMoving, isVisible }: { isMoving: boolean; isVisible: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const stateRef = useRef({
    creamRadius: 99999,      // cream circle radius (starts fully cream)
    creamTarget: 99999,
    darkAlpha: 0,            // dark overlay opacity (0 = no dark, 1 = fully dark)
    darkTarget: 0,
  });
  const prevMovingRef = useRef<boolean | null>(null);
  const frameRef = useRef(0);

  // Track mouse position
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  // Update targets on isMoving change
  useEffect(() => {
    if (prevMovingRef.current === isMoving) return;
    prevMovingRef.current = isMoving;

    const s = stateRef.current;
    if (isMoving) {
      // Going dark: fade overlay in, leave cream circle as-is (it gets covered)
      s.darkTarget = 1;
    } else {
      // Going cream: fade overlay out + expand cream circle from cursor
      s.darkTarget = 0;
      s.creamRadius = 0; // reset to cursor position
      const cw = window.innerWidth * 1.2;
      const ch = window.innerHeight * 1.2;
      const offsetX = window.innerWidth * 0.1;
      const offsetY = window.innerHeight * 0.1;
      const cx = mouseRef.current.x + offsetX;
      const cy = mouseRef.current.y + offsetY;
      // Distance to farthest corner × 3 so the solid zone (inner 35%) covers everything
      const maxDist = Math.max(
        Math.sqrt(cx * cx + cy * cy),
        Math.sqrt((cw - cx) ** 2 + cy * cy),
        Math.sqrt(cx * cx + (ch - cy) ** 2),
        Math.sqrt((cw - cx) ** 2 + (ch - cy) ** 2)
      );
      s.creamTarget = maxDist * 3;
    }
  }, [isMoving]);

  // Canvas animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = Math.ceil(window.innerWidth * 1.2);
      canvas.height = Math.ceil(window.innerHeight * 1.2);
    };
    resize();
    window.addEventListener('resize', resize);

    const creamSolid = WAVE_CONFIG.cream.solid;
    const darkSolid = WAVE_CONFIG.dark.solid;

    const animate = () => {
      const s = stateRef.current;
      const cw = canvas.width;
      const ch = canvas.height;

      // Lerp cream radius
      s.creamRadius += (s.creamTarget - s.creamRadius) * 0.02;
      if (Math.abs(s.creamRadius - s.creamTarget) < 1) s.creamRadius = s.creamTarget;

      // Lerp dark overlay alpha
      const darkLerp = s.darkTarget === 1 ? 0.08 : 0.04;
      s.darkAlpha += (s.darkTarget - s.darkAlpha) * darkLerp;
      if (Math.abs(s.darkAlpha - s.darkTarget) < 0.005) s.darkAlpha = s.darkTarget;

      // Origin follows mouse (offset for 120% canvas)
      const offsetX = window.innerWidth * 0.1;
      const offsetY = window.innerHeight * 0.1;
      const cx = mouseRef.current.x + offsetX;
      const cy = mouseRef.current.y + offsetY;

      // 1. Dark base
      ctx.globalAlpha = 1;
      ctx.fillStyle = darkSolid;
      ctx.fillRect(0, 0, cw, ch);

      // 2. Cream circle with soft falloff
      const r = s.creamRadius;
      if (r > 0) {
        ctx.globalAlpha = 1;
        const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        gradient.addColorStop(0, creamSolid);
        gradient.addColorStop(0.35, creamSolid);
        gradient.addColorStop(0.55, 'rgba(245, 243, 241, 0.75)');
        gradient.addColorStop(0.72, 'rgba(245, 243, 241, 0.4)');
        gradient.addColorStop(0.88, 'rgba(245, 243, 241, 0.1)');
        gradient.addColorStop(1, 'rgba(245, 243, 241, 0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, cw, ch);
      }

      // 3. Dark overlay fade (covers everything when going dark)
      if (s.darkAlpha > 0) {
        ctx.globalAlpha = s.darkAlpha;
        ctx.fillStyle = darkSolid;
        ctx.fillRect(0, 0, cw, ch);
        ctx.globalAlpha = 1;
      }

      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute"
      style={{
        top: '-10vh',
        left: '-10vw',
        width: '120vw',
        height: '120vh',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
      }}
    />
  );
}

// ============================================
// NOISE EFFECT COMPONENT (radiating grain from cursor)
// ============================================
function NoiseEffect() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -100, y: -100 });
  const prevMouseRef = useRef({ x: -100, y: -100 });
  const intensityRef = useRef(0);
  const frameRef = useRef(0);
  const [mousePos, setMousePos] = useState({ x: -100, y: -100 });
  const [rotation, setRotation] = useState(0);
  const rotationRef = useRef(0);

  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - prevMouseRef.current.x;
      const dy = e.clientY - prevMouseRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      prevMouseRef.current = { ...mouseRef.current };
      mouseRef.current = { x: e.clientX, y: e.clientY };
      setMousePos({ x: e.clientX, y: e.clientY });

      rotationRef.current += distance * TRAIL_CONFIG.spinSpeed;
      setRotation(rotationRef.current);

      intensityRef.current = Math.min(1, intensityRef.current + distance * NOISE_CONFIG.intensityBoost);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Canvas animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const { colors, maxRadius, maxParticles, grainMin, grainMax, trailFade, intensityDecay } = NOISE_CONFIG;
    const grainRange = grainMax - grainMin;

    const animate = () => {
      // Fade existing content toward transparency (trails linger then vanish)
      ctx.globalCompositeOperation = 'destination-out';
      ctx.globalAlpha = trailFade;
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;

      const intensity = intensityRef.current;

      if (intensity > 0.01) {
        const { x, y } = mouseRef.current;
        const radius = maxRadius * intensity;
        const count = Math.floor(maxParticles * intensity);

        for (let i = 0; i < count; i++) {
          const angle = Math.random() * Math.PI * 2;
          // sqrt distribution for even radial spread
          const dist = Math.sqrt(Math.random()) * radius;
          const px = x + Math.cos(angle) * dist;
          const py = y + Math.sin(angle) * dist;

          if (px < 0 || px > canvas.width || py < 0 || py > canvas.height) continue;

          const color = colors[Math.floor(Math.random() * 3)];
          const size = grainMin + Math.random() * grainRange;
          // Opacity falls off with distance from cursor
          const distRatio = dist / radius;
          const alpha = (1 - distRatio * distRatio) * intensity * 0.95;

          ctx.globalAlpha = alpha;
          ctx.fillStyle = color;
          ctx.fillRect(Math.floor(px), Math.floor(py), size, size);
        }

        ctx.globalAlpha = 1;
        intensityRef.current *= intensityDecay;
      } else {
        intensityRef.current = 0;
      }

      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[110]">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      {/* Main cursor ∅ */}
      <div
        className="fixed z-[120]"
        style={{
          left: mousePos.x - TRAIL_CONFIG.cursorSize / 2,
          top: mousePos.y - TRAIL_CONFIG.cursorSize / 2,
        }}
      >
        <svg
          width={TRAIL_CONFIG.cursorSize}
          height={TRAIL_CONFIG.cursorSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          style={{ transform: `rotate(${rotation}deg)` }}
          className="text-[#CF6666]"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    </div>
  );
}

// ============================================
// ARTIST MODAL COMPONENT
// ============================================
function ArtistModal({ onClose }: { onClose: () => void }) {
  const [isBackdropVisible, setIsBackdropVisible] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const moveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track actual viewport height for Chrome iOS tablet fix
  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if desktop (lg breakpoint: 1024px)
  useEffect(() => {
    const lgQuery = window.matchMedia('(min-width: 1024px)');
    setIsDesktop(lgQuery.matches);
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    lgQuery.addEventListener('change', handleChange);
    return () => lgQuery.removeEventListener('change', handleChange);
  }, []);

  // Scramble effect for artist statement using library
  const { ref: scrambleRef, replay } = useScrambleLib({
    text: ARTIST_STATEMENT,
    speed: 0.6,
    tick: 1,
    step: 5,
    scramble: 8,
    seed: 3,
    chance: 1,
    overdrive: false,
    range: [97, 122],  // lowercase a-z only
    ignore: [' ', '\n', '.', ',', '\'', '"', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
    playOnMount: false,
  });

  // Trigger scramble animation when mouse STARTS moving
  useEffect(() => {
    if (isMoving) {
      replay();
    }
  }, [isMoving, replay]);

  // Fade in on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      setIsBackdropVisible(true);
      setIsContentVisible(true);
    });
  }, []);

  // Handle close with sequential fade out (content first, then backdrop - matches lightbox)
  const handleClose = () => {
    setIsContentVisible(false);  // Content fades first (500ms)
    setTimeout(() => {
      setIsBackdropVisible(false);  // Then backdrop fades (500ms)
      setTimeout(onClose, 520);  // Then unmount
    }, 520);
  };

  // Keyboard handler + body overflow
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, []);


  // Fade detection - desktop only
  useEffect(() => {
    if (!isDesktop) return;

    const handleMouseMove = () => {
      setIsMoving(true);

      // Clear pending timeout and reschedule
      if (moveTimeout.current) {
        clearTimeout(moveTimeout.current);
      }

      // Schedule fade to clear after mouse stops
      moveTimeout.current = setTimeout(() => {
        setIsMoving(false);
      }, FADE_CONFIG.clearDelay);
    };

    // Listen on the container, not window
    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => {
        container.removeEventListener('mousemove', handleMouseMove);
        if (moveTimeout.current) clearTimeout(moveTimeout.current);
      };
    }
  }, [isDesktop]);

  return createPortal(
    <div
      ref={containerRef}
      className={`fixed top-0 left-0 w-full z-[100] ${isDesktop ? 'cursor-none' : ''}`}
      style={{
        height: viewportHeight,
        transform: 'translate3d(0, 0, 0)', // Force GPU acceleration for iOS
      }}
      onClick={handleClose}
    >
      {/* Backdrop - wave effect on desktop, simple dark on mobile */}
      {isDesktop ? (
        <WaveBackdrop isMoving={isMoving} isVisible={isBackdropVisible} />
      ) : (
        <div
          className="absolute bg-[#1B1B1D]"
          style={{
            top: '-10vh',
            left: '-10vw',
            width: '120vw',
            height: '120vh',
            opacity: isBackdropVisible ? 1 : 0,
            transition: 'opacity 500ms ease-in-out',
          }}
        />
      )}

      {/* Noise effect + cursor - desktop only */}
      {isDesktop && <NoiseEffect />}

      {/* Content wrapper - fades first on close (like photo in lightbox) */}
      <div
        className="absolute inset-0"
        style={{
          opacity: isContentVisible ? 1 : 0,
          transition: 'opacity 500ms ease-in-out',
          pointerEvents: isContentVisible ? 'auto' : 'none',
        }}
      >
        {/* Content - scrambles when moving (desktop only) */}
        <div className="absolute inset-0 flex items-center justify-center px-4 pb-20 md:p-8 pointer-events-none">
          <div
            className={`relative max-w-2xl max-h-[72vh] md:max-h-[80vh] overflow-y-auto font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[17px] leading-[1.3] tracking-[0.2px] pointer-events-auto ${isDesktop ? 'cursor-none' : ''}`}
            style={{
              color: isDesktop ? (isMoving ? '#F5F3F1' : '#594e4e') : '#F5F3F1',
              opacity: (isDesktop && isMoving) ? FADE_CONFIG.fadedOpacity : 1,
              transition: `opacity ${isMoving ? FADE_CONFIG.fadeIn : FADE_CONFIG.fadeOut}ms ease-in-out, color ${isMoving ? FADE_CONFIG.fadeIn : FADE_CONFIG.fadeOut}ms ease-in-out`,
            }}
          >
            <p className="text-[#CF6666] mb-6">Purpose</p>
            {(isDesktop && isMoving) ? (
              <p ref={scrambleRef} className="whitespace-pre-wrap" />
            ) : (
              <p className="whitespace-pre-wrap">{ARTIST_STATEMENT}</p>
            )}
            {/* Email - visible on mobile always, hidden on desktop (shown in stillness overlay instead) */}
            {!isDesktop && (
              <a
                href="mailto:alive@dullworld.xyz"
                className="block mt-8 text-[#CF6666] hover:underline"
              >
                alive@dullworld.xyz
              </a>
            )}
          </div>
        </div>

        {/* "stillness reveals" - fades in when moving (desktop only) */}
        {isDesktop && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none z-[105]"
            style={{
              opacity: isMoving ? 1 : 0,
              transition: 'opacity 800ms ease-in-out',
            }}
          >
            <p className="font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[28px] tracking-[0.3px] text-[#F5F3F1]">
              stillness reveals
            </p>
          </div>
        )}

        {/* Mobile/tablet close button - center bottom */}
        <button
          onClick={(e) => { e.stopPropagation(); handleClose(); }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[110] text-[#CF6666]/60 hover:text-[#CF6666] transition-colors lg:hidden pointer-events-auto"
          aria-label="Close"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
}

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  // Splash sequence: text fades in, holds, fades out, then content fades in
  const [splashTextVisible, setSplashTextVisible] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const [splashComplete, setSplashComplete] = useState(false);
  const [randomLine] = useState(() =>
    SPLASH_LINES[Math.floor(Math.random() * SPLASH_LINES.length)]
  );

  const [textLight, setTextLight] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [evenColumns, setEvenColumns] = useState(false); // Even columns (2,4) stay dark, odd (1,3) go light
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);

  // Splash sequence timing
  useEffect(() => {
    // Fade in splash text
    const fadeInText = setTimeout(() => setSplashTextVisible(true), 200);

    // Fade out splash text (after 3 seconds total)
    const fadeOutText = setTimeout(() => setSplashTextVisible(false), 3000);

    // Fade in content (520ms after text starts fading)
    const fadeInContent = setTimeout(() => setContentVisible(true), 3520);

    // Mark splash complete
    const complete = setTimeout(() => setSplashComplete(true), 4040);

    return () => {
      clearTimeout(fadeInText);
      clearTimeout(fadeOutText);
      clearTimeout(fadeInContent);
      clearTimeout(complete);
    };
  }, []);

  // Global mouse tracking for tooltip (keeps following during fade out)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setTooltipPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Track column count for ∅ color logic
  // 1 col (<640px): odd → fade | 2 cols (640-1023px): even → dark
  // 3 cols (1024-1279px): odd → fade | 4 cols (≥1280px): even → dark
  useEffect(() => {
    const smQuery = window.matchMedia('(min-width: 640px)');
    const lgQuery = window.matchMedia('(min-width: 1024px)');
    const xlQuery = window.matchMedia('(min-width: 1280px)');

    const updateColumns = () => {
      const isSm = smQuery.matches;
      const isLg = lgQuery.matches;
      const isXl = xlQuery.matches;
      // Even: 2 cols (sm but not lg) OR 4 cols (xl)
      setEvenColumns((isSm && !isLg) || isXl);
    };

    updateColumns();
    smQuery.addEventListener('change', updateColumns);
    lgQuery.addEventListener('change', updateColumns);
    xlQuery.addEventListener('change', updateColumns);
    return () => {
      smQuery.removeEventListener('change', updateColumns);
      lgQuery.removeEventListener('change', updateColumns);
      xlQuery.removeEventListener('change', updateColumns);
    };
  }, []);

  // Scroll handler for header text color
  useEffect(() => {
    const handleScroll = () => {
      setTextLight(window.scrollY > 76);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Simplified: showContent controls both - when false, splash shows; when true, content shows
  const showContent = contentVisible;

  return (
    <div className="min-h-screen bg-[#F5F3F1] antialiased [text-rendering:optimizeLegibility]">

      {/* Splash text - fades out as content fades in */}
      <div
        className="fixed inset-0 z-50 flex flex-col items-center justify-center pointer-events-none"
        style={{
          opacity: showContent ? 0 : (splashTextVisible ? 1 : 0),
          transition: 'opacity 800ms ease-in-out',
        }}
      >
        <p className="font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[17px] tracking-[0.2px] text-[#594e4e] text-center px-8">
          {randomLine}
        </p>
        <p className="font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[17px] tracking-[0.2px] text-[#CF6666]">
          dullworld.xyz
        </p>
      </div>

      {/* Main content - fades in as splash fades out */}
      <div
        style={{
          opacity: showContent ? 1 : 0,
          transition: 'opacity 800ms ease-in-out',
        }}
      >

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 pointer-events-none font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[17px] leading-[1.3] tracking-[0.2px]">

        {/* Mobile layout */}
        <div className="md:hidden px-4 pt-[6px] pb-5 relative">
          <div className="pointer-events-auto mt-[29px] ml-[2px]">
            <p className="text-[#CF6666]">dullworld.xyz</p>
            <p className={`transition-colors duration-2000 ${textLight ? 'text-[#E8E4E1]' : 'text-[#594e4e]'}`}>
              desire obscures wisdom
            </p>
          </div>

          <div className={`pointer-events-auto mt-2 ml-[2px] transition-opacity duration-1000 ${textLight ? 'opacity-0' : 'opacity-100'}`}>
            <p className="text-[#594e4e]">_</p>
            <p className="text-[#594e4e]"><em>perspectives</em></p>
            <p className="text-[#594e4e]">crystallised by Dylan Galletly</p>
          </div>
        </div>

        {/* Desktop layout */}
        <div className="hidden md:flex relative px-8 lg:px-12 py-6 justify-between items-start">
          <div className="pointer-events-auto px-[2px] flex-1 leading-[1.3] mt-[0px] self-end">
            <p className={`transition-colors duration-2000 ${textLight ? 'text-[#E8E4E1]' : 'text-[#594e4e]'}`}>_</p>
            <p className={`transition-colors duration-2000 ${textLight ? 'text-[#E8E4E1]' : 'text-[#594e4e]'}`}>
              <em>perspectives</em>
            </p>
            <p className={`transition-colors duration-2000 ${textLight ? 'text-[#E8E4E1]' : 'text-[#594e4e]'}`}>
              crystallised by Dylan Galletly
            </p>
          </div>

          <div className="pointer-events-auto flex-1 flex justify-center pt-0">
            <button
              onClick={() => setShowStatement(true)}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className={`
                font-light transition-colors duration-500 ease-out hover:text-[#CF6666]
                mt-0 p-4 -m-4
                ${evenColumns ? 'text-[#594e4e]' : (textLight ? 'text-[#E8E4E1]' : 'text-[#594e4e]')}
              `}
              aria-label="Artist statement"
            >
              <span style={{ fontSize: '26px' }}>∅</span>
            </button>
          </div>

          <div className="pointer-events-auto text-right px-[2px] flex-1 leading-[1.3] self-end">
            <p className="text-[#CF6666]">dullworld.xyz</p>
            <p className={`transition-colors duration-2000 ${textLight ? 'text-[#E8E4E1]' : 'text-[#594e4e]'}`}>
              desire obscures wisdom
            </p>
          </div>
        </div>
      </header>

      {/* Mobile ∅ button - fixed bottom center */}
      <button
        onClick={() => setShowStatement(true)}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 md:hidden pointer-events-auto text-[#F5F3F1] hover:text-[#CF6666] transition-colors duration-500"
        aria-label="Artist statement"
      >
        <span className="font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif]" style={{ fontSize: '40px' }}>∅</span>
      </button>

      {/* Gallery */}
      <main className="pt-[calc(var(--spacing)*50)] md:pt-[calc(var(--spacing)*35)]">
        <Gallery ready={splashComplete} />
      </main>

      {/* Footer */}
      <footer className="px-4 md:px-8 lg:px-12 py-12 flex flex-col items-center gap-0 md:flex-row md:justify-between font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[17px] tracking-[0.2px]">
        <p className="text-[#5C5C5C]">© 2026 dullworld™</p>
        <a
          href="mailto:alive@dullworld.xyz"
          className="text-[#CF6666] hover:text-[#848CC7] transition-colors"
        >
          alive@dullworld.xyz
        </a>
      </footer>

      {/* Follow mouse tooltip for ∅ - desktop only */}
      <div
        className="fixed pointer-events-none z-50 hidden md:block font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[17px] tracking-[0.2px] text-[#CF6666]"
        style={{
          left: tooltipPos.x + 16,
          top: tooltipPos.y + 16,
          opacity: showTooltip ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
      >
        purpose
      </div>

      </div>{/* End main content wrapper */}

      {/* Artist Statement Modal */}
      {showStatement && <ArtistModal onClose={() => setShowStatement(false)} />}
    </div>
  );
}

export default App;
